import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { profile } from '../mock';

const Hero = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const y1 = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      ref={containerRef}
      className="relative min-h-screen overflow-hidden bg-background flex items-center"
    >
      {/* Background decorations */}
      <div className="absolute inset-0 bg-grid-dark opacity-20 pointer-events-none" />
      <div
        className="absolute top-1/3 -left-64 w-[700px] h-[700px] rounded-full opacity-[0.06] blur-[120px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #d97757 0%, transparent 70%)' }}
      />
      <div
        className="absolute bottom-0 -right-64 w-[600px] h-[600px] rounded-full opacity-[0.06] blur-[120px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #f4a261 0%, transparent 70%)' }}
      />

      <motion.div
        style={{ y: y1, opacity }}
        className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-28 pb-16 flex flex-col lg:flex-row items-center justify-between gap-16"
      >
        {/* Text side */}
        <div className="flex-1 flex flex-col items-start text-left">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            <span className="text-xs mono uppercase tracking-widest text-slate-300">
              Available for select engagements
            </span>
          </motion.div>

          <h1 className="serif font-semibold leading-[0.9] tracking-tight text-white mb-8">
            <motion.span
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.35, type: 'spring', damping: 18 }}
              className="block text-[clamp(52px,10vw,120px)]"
            >
              PRADEEP
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5, type: 'spring', damping: 18 }}
              className="block italic text-primary text-[clamp(52px,10vw,120px)]"
            >
              PABOLU
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="text-lg sm:text-xl text-slate-400 font-light max-w-lg leading-relaxed"
          >
            Senior Cloud Solutions Architect — designing enterprise Azure migrations,
            Landing Zones, and modern DevOps for the team that runs it on a Tuesday.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollTo('contact')}
              className="px-7 py-3.5 rounded-full bg-primary hover:bg-primary/90 text-white text-sm font-medium tracking-wide transition-all hover:scale-105 hover:shadow-lg hover:shadow-primary/30"
            >
              Start a project
            </button>
            <button
              onClick={() => scrollTo('about')}
              className="px-7 py-3.5 rounded-full border border-white/15 bg-white/5 text-slate-300 text-sm font-medium tracking-wide hover:border-primary/60 hover:text-white transition-all"
            >
              Explore work ↓
            </button>
          </motion.div>

          {/* Stats strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="mt-14 flex flex-wrap gap-10"
          >
            {[
              { val: '20+', label: 'Years Experience' },
              { val: '120+', label: 'Projects Delivered' },
              { val: '50+', label: 'Enterprise Clients' },
            ].map((s) => (
              <div key={s.label}>
                <div className="serif text-3xl text-white font-semibold">{s.val}</div>
                <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Portrait side */}
        <motion.div
          style={{ y: y2 }}
          initial={{ opacity: 0, scale: 0.85, rotate: -3 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.2, delay: 0.8, type: 'spring', damping: 20 }}
          className="relative shrink-0 w-56 sm:w-72 lg:w-80 aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl border border-white/10"
        >
          {/* Subtle terracotta overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-primary/30 via-transparent to-transparent z-10 pointer-events-none" />
          <img
            src={profile.photo}
            alt="Pradeep Pabolu"
            className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
          />
          {/* Badge */}
          <div className="absolute bottom-4 left-4 right-4 z-20 p-3 rounded-xl bg-black/60 backdrop-blur-lg border border-white/10">
            <div className="text-white font-medium text-sm">{profile.name}</div>
            <div className="mono text-[10px] uppercase tracking-widest text-primary mt-0.5">{profile.role}</div>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        onClick={() => scrollTo('about')}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-500 hover:text-primary transition-colors z-20"
      >
        <span className="mono text-[9px] uppercase tracking-widest">Scroll</span>
        <div className="w-5 h-8 rounded-full border border-current flex justify-center pt-1.5">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}
            className="w-0.5 h-1.5 rounded-full bg-current"
          />
        </div>
      </motion.button>
    </section>
  );
};

export default Hero;
